import { computed, Injectable, signal } from "@angular/core";
import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import { cognitoConfig } from "./cognito.config";

const TOKEN_KEY = "do_your_shades_admin_token";
const REFRESH_KEY = "do_your_shades_admin_refresh";
const USER_KEY = "do_your_shades_admin_user";
const LAST_ACTIVE_KEY = "do_your_shades_admin_last_active";

export const IDLE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

function decodePayload(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split(".")[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  const p = decodePayload(token);
  if (!p || typeof p["exp"] !== "number") return false;
  return Date.now() >= (p["exp"] as number) * 1000;
}

@Injectable({ providedIn: "root" })
export class AuthService {
  private pool: CognitoUserPool;

  private userSignal = signal<AdminUser | null>(this.loadStoredUser());
  private tokenSignal = signal<string | null>(
    localStorage.getItem(TOKEN_KEY),
  );
  private refreshTokenValue: string | null = localStorage.getItem(REFRESH_KEY);

  readonly user = this.userSignal.asReadonly();
  readonly token = this.tokenSignal.asReadonly();
  readonly isAuthenticated = computed(() => {
    const t = this.tokenSignal();
    return t !== null && !isExpired(t);
  });

  constructor() {
    const { userPoolId, clientId } = cognitoConfig();
    this.pool =
      userPoolId && clientId
        ? new CognitoUserPool({
            UserPoolId: userPoolId,
            ClientId: clientId,
          })
        : (null as unknown as CognitoUserPool);
  }

  private requirePool(): CognitoUserPool {
    if (!this.pool) {
      throw new Error("Auth is not configured.");
    }
    return this.pool;
  }

  private loadStoredUser(): AdminUser | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as AdminUser) : null;
    } catch {
      return null;
    }
  }

  private persist(token: string, refreshToken: string, user: AdminUser) {
    this.tokenSignal.set(token);
    this.refreshTokenValue = refreshToken;
    this.userSignal.set(user);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  async login(email: string, password: string): Promise<void> {
    const details = new AuthenticationDetails({
      Username: email,
      Password: password,
    });
    const user = new CognitoUser({
      Username: email,
      Pool: this.requirePool(),
    });
    // SRP (the library default) — the admin app client allows USER_SRP_AUTH.
    const session = await new Promise<{
      idToken: string;
      refreshToken: string;
      payload: Record<string, string>;
    }>((resolve, reject) => {
      user.authenticateUser(details, {
        onSuccess: (s) =>
          resolve({
            idToken: s.getIdToken().getJwtToken(),
            refreshToken: s.getRefreshToken().getToken(),
            payload: s.getIdToken().payload as Record<string, string>,
          }),
        onFailure: (e) => reject(e),
      });
    });
    // The pool provisions admins with custom:role "super_admin"; normalize to
    // "admin" so guards and the UI treat it as the admin role.
    const role =
      (session.payload["custom:role"] ?? "").toLowerCase() === "super_admin"
        ? "admin"
        : (session.payload["custom:role"] ?? "");
    if (role !== "admin") {
      await this.logout();
      throw new Error("This account is not an admin.");
    }
    localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
    this.persist(session.idToken, session.refreshToken, {
      id: session.payload["sub"] ?? "",
      email: session.payload["email"] ?? email,
      name: session.payload["name"] ?? "",
      role,
    });
  }

  /** Silent refresh using the stored refresh token; false if unusable. */
  async restoreSession(): Promise<boolean> {
    const t = this.tokenSignal();
    if (!t) return false;
    if (!isExpired(t)) return true;
    return this.refresh();
  }

  /** Refresh only if the current idToken is within `marginMs` of expiring. */
  async refreshIfNeeded(marginMs = 60_000): Promise<void> {
    const token = this.tokenSignal();
    if (!token) return;
    const p = decodePayload(token);
    const exp = p?.["exp"];
    if (typeof exp !== "number") return;
    if (Date.now() >= (exp * 1000 - marginMs)) {
      await this.refresh();
    }
  }

  async refresh(): Promise<boolean> {
    const email = this.userSignal()?.email;
    if (!email || !this.refreshTokenValue) {
      this.logout();
      return false;
    }
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.requirePool(),
    });
    const refreshToken = new CognitoRefreshToken({
      RefreshToken: this.refreshTokenValue,
    });
    try {
      await new Promise<void>((resolve, reject) => {
        cognitoUser.refreshSession(refreshToken, (err, session) => {
          if (err) {
            reject(err);
            return;
          }
          const user = this.userSignal();
          this.persist(
            session.getIdToken().getJwtToken(),
            session.getRefreshToken().getToken(),
            user!,
          );
          resolve();
        });
      });
      return true;
    } catch {
      this.logout();
      return false;
    }
  }

  idleExceeded(): boolean {
    const t = this.tokenSignal();
    if (!t) return false;
    const last = Number(
      localStorage.getItem(LAST_ACTIVE_KEY) ?? Date.now(),
    );
    return Date.now() - last > IDLE_MS;
  }

  touch() {
    localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
  }

  async logout(): Promise<void> {
    const email = this.userSignal()?.email;
    if (email) {
      try {
        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: this.requirePool(),
        });
        await new Promise<void>((resolve) =>
          cognitoUser.globalSignOut({
            onSuccess: () => resolve(),
            onFailure: () => resolve(),
          }),
        );
      } catch {
        // best-effort; local session is cleared regardless.
      }
    }
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    this.refreshTokenValue = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_ACTIVE_KEY);
  }
}
